import _ from 'lodash';
import { calcCurrentPrice, calcCurrentDuration } from '../../../utils/packagesUtils';

export const getRecommendedType = (id, { pckg_id, tier_selection }) => {
    if (pckg_id === id.toString() && (tier_selection === 'recommended' || tier_selection === 'customized')) {
        return tier_selection;
    } else {
        return '';
    }
};

export const sortPackages = (packageList) => {
    packageList.sort((package1, package2) => {
        if (package1.id < package2.id) {
            return -1;
        } else {
            return 1;
        }
    });
};

export const isFeatureFiltered = (feature, filteredFactors) => {
    const filteredFeature = filteredFactors.find((filteredFactor) => filteredFactor === feature.name);

    return !!filteredFeature;
};

const mapPackagesFeatures = (packageList, requestParams) => {
    const mappedFeatures = {};
    const includedFeatureIds = new Set();

    const filteredFactors = getFilteredFactors(requestParams);
    const filteredPackageId = requestParams.pckg_id ? parseInt(requestParams.pckg_id, 10) : -1;

    packageList.forEach((packageData) => {
        packageData.features.forEach((feature) => {
            if (!mappedFeatures[feature.id]) {
                mappedFeatures[feature.id] = {
                    featureData: feature,
                    includedCount: 0
                };
            }

            if (feature.included) {
                mappedFeatures[feature.id].includedCount++;
                includedFeatureIds.add(feature.id);
            } else if (filteredPackageId === packageData.id && isFeatureFiltered(feature, filteredFactors)) {
                feature.included = true;
                feature.customized = true;
                mappedFeatures[feature.id].includedCount++;
                includedFeatureIds.add(feature.id);
            }
        });
    });

    return {
        mappedFeatures,
        includedFeatureIds
    };
};

export const alignPackagesFeatures = (packageList, requestParams) => {
    if (packageList.length !== 3) {
        return;
    }

    const { mappedFeatures, includedFeatureIds } = mapPackagesFeatures(packageList, requestParams);

    packageList.forEach((packageData) => {
        // filter features which are not included in all 3 packages
        packageData.features = packageData.features.filter((feature) => mappedFeatures[feature.id].includedCount > 0);

        // fill/filter features lists which are smaller/bigger than the featureIds list
        const { features } = packageData;
        if (features.length < includedFeatureIds.size) {
            includedFeatureIds.forEach((featureId) => {
                if (features.find((feature) => feature.id === featureId) === undefined) {
                    features.push({ ...mappedFeatures[featureId].featureData, included: false, customized: false });
                }
            });
        } else if (features.length > includedFeatureIds.size) {
            packageData.features = features.filter((feature) => includedFeatureIds.has(feature.id));
        }
    });
};

export const sortPackagesFeatures = (packageList) => {
    const featuresOrder = {};
    let orderIndex = 1;

    packageList.slice().reverse().forEach((packageData) => {
        packageData.features.forEach((feature) => {
            if (feature.included && !featuresOrder[feature.id]) {
                featuresOrder[feature.id] = orderIndex++;
            }
        });
    });

    packageList.forEach((packageData) => {
        packageData.features.sort((feature1, feature2) => {
            const featureOrder1 = featuresOrder[feature1.id],
                featureOrder2 = featuresOrder[feature2.id];
            if (featureOrder1 < featureOrder2) {
                return -1;
            } else {
                return 1;
            }
        });
    });
};

const getFilteredFactors = (requestParams) => {
    const filteredFactorsStr = requestParams.filtered_factors;
    return filteredFactorsStr ? filteredFactorsStr.split(',') : [];
};

const formatCustomExtras = (customExtras) => customExtras.map((customExtra) => {
    const formattedDuration = formatDuration(customExtra.duration);
    return {
        ...customExtra,
        duration: formattedDuration,
        currentDuration: formattedDuration
    };
});

const formatFeatures = (features, requestParams) => {
    const filteredFactors = getFilteredFactors(requestParams);

    return features.map((feature) => {
        const isFilteredFactor = !!filteredFactors.find((filteredFactor) => filteredFactor === feature.name);
        const amount = isFilteredFactor ? 1 : 0;
        const currentPrice = feature.price;
        const formattedDuration = formatDuration(feature.duration);

        return { ...feature, duration: formattedDuration, amount, currentPrice, currentDuration: formattedDuration };
    });
};

const formatCalculators = (calculators) => calculators.map((calculator) => ({
    ...calculator,
    currentValue: calculator.value
}));

const formatExtraFast = (extraFast, { extra_fast }) => {
    const duration = formatDuration(extraFast.duration);
    const currentValue = extra_fast ? 1 : 0;
    const currentPrice = extraFast.price;

    return { ...extraFast, currentValue, currentPrice, duration, currentDuration: duration };
};

const formatDuration = (duration) => duration > 0 ? duration / 24 : 0;

export const getNewPrice = (packageData, newCurrentPrice) => {
    // If new calculated price is lower than the initial package price we'll set it to be the initial price
    // The calculator itself will still be updated with the inserted value
    if (newCurrentPrice < packageData.price) {
        newCurrentPrice = packageData.price;
    }

    return newCurrentPrice;
};

const formatPackages = (packageList, requestParams) => {
    sortPackages(packageList);
    alignPackagesFeatures(packageList, requestParams);
    sortPackagesFeatures(packageList);
};

export default (packages, requestContext) => {
    let packageList = [];
    const { params } = requestContext;

    if (packages && packages.packageList) {
        const clonedPackages = _.cloneDeep(packages);
        formatPackages(clonedPackages.packageList, params);
        packageList = clonedPackages.packageList;
    }

    return packageList.map((packageData) => {
        const { duration, id, calculators, extraFast, features, customExtras } = packageData;

        const formattedPackageData = {
            ...packageData,
            duration: formatDuration(duration),
            calculators: calculators ? formatCalculators(calculators) : [],
            extraFast: extraFast ? formatExtraFast(extraFast, params) : null,
            recommendedType: getRecommendedType(id, params),
            features: features ? formatFeatures(features, params) : [],
            customExtras: customExtras ? formatCustomExtras(customExtras) : []
        };

        return {
            ...formattedPackageData,
            currentPrice: calcCurrentPrice(formattedPackageData),
            currentDuration: calcCurrentDuration(formattedPackageData)
        };
    });
};
